import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Modal } from 'react-bootstrap';
import KanbanBoard from './KanbanBoard';
import OrderDetailsModal from './OrderDetailsModal';
import SaleDetailsDrawer from './modals/SaleDetailsDrawer';
import CancelShipmentDrawer from './CancelShipmentDrawer';
import ReportShipmentDrawer from './ReportShipmentDrawer';
import TagsModal from './components/TagsModal';
import cardTagsService from '../../services/cardTagsService';
import { API_URL } from '../../config/api';
import './OrderDetailsModal.css';
import { getProductPrice, formatCurrency, hasPlans } from '../../utils/productPricing';
import styles from './afterpay.module.scss';
import { productsV2API } from '../../services/api/v2/products';
import { salesV2API } from '../../services/api/v2/sales';
import CurrencyFormatter from '../../utils/CurrencyFormatter';

/**
 * 🧮 deriveSaleItemColumn(item, sale)  ← KAN-13 / §9.2
 *
 * Spec V2 §9.2: "1 card por sale_item (não por sale)". Cada produto físico
 * vendido vira um card independente no Kanban, com seu próprio status,
 * tracking_code e ações (cancelar envio, reportar pedido).
 *
 * Esta função recebe 1 sale_item e devolve a coluna onde o card deve cair.
 * Substitui `deriveSaleDeliveryStatus` (agregação por venda) — mantida abaixo
 * por compat com outros chamadores eventuais.
 */
export const deriveSaleItemColumn = (item, sale) => {
  const metadata = sale?.metadata || {};
  // Reportados — erro de validação na venda inteira impacta todo card da venda
  if (metadata.validation_errors && metadata.validation_errors.length > 0) {
    return 'reportados';
  }
  const itemMeta = item?.metadata || {};
  const status = item?.status;

  if (status === 'cancelled' || status === 'refunded') return 'frustrados';
  // KAN-BUG-01 defesa em profundidade: se o backend TrackingService ainda não
  // conseguiu subir sale_items.status='delivered' (deploy pendente, retry
  // pendente), mas metadata.tracking_status já mostra entregue OU delivered_at
  // foi carimbado, tratamos como entregue pra não travar em "saiu_entrega".
  if (
    status === 'delivered'
    || item?.delivered_at
    || itemMeta.tracking_status === 'delivered'
  ) {
    if (sale?.payment_status === 'paid') return 'pagos';
    return 'entregue';
  }
  if (status === 'failed_attempt' || status === 'returned') return 'requer_atencao';
  if (status === 'available_for_pickup') return 'retirar_correios';
  if (itemMeta.tracking_status === 'out_for_delivery' || status === 'shipped') {
    return 'saiu_entrega';
  }
  if (item?.tracking_code) return 'enviados';
  return 'expedicao';
};

/**
 * 🧮 deriveSaleDeliveryStatus(saleItems, sale) — DEPRECATED
 *
 * Em V2 a tabela `sales` não tem mais `sale_status` — o status logístico
 * vive em `sale_items.status` (1 status por item). Cada venda pode ter
 * N itens, então o status "global" da venda (= coluna do Kanban) é
 * derivado agregando os status dos itens.
 *
 * Valores possíveis de `sale_items.status` (enum `sale_item_status`,
 * migration V2_013): pending | processing | shipped | available_for_pickup
 * | failed_attempt | returned | delivered | cancelled | refunded.
 *
 * Mapeamento → coluna do Kanban (mesmas 10 colunas do V1):
 *  - 'reportados'        : venda com `metadata.validation_errors` (CPF/CEP)
 *  - 'expedicao'         : algum item com tracking_code mas sem status Correios
 *  - 'enviados'          : algum item `processing`/`shipped` (sem tracking)
 *  - 'saiu_entrega'      : algum item `shipped` com `tracking_status='out_for_delivery'`
 *  - 'retirar_correios'  : algum item `available_for_pickup`
 *  - 'requer_atencao'    : algum item `failed_attempt` ou `returned`
 *  - 'entregue'          : TODOS itens `delivered` E venda não está paga ainda
 *  - 'inadimplencia'     : todos itens `delivered` mas paga >X dias atrás sem split
 *  - 'frustrados'        : todos itens `cancelled`/`refunded`
 *  - 'pagos'             : todos itens `delivered` E `payment_status='paid'`
 *
 * Para o cenário inicial V2 (sem rastreamento real conectado), as regras
 * mais simples são suficientes — refinamentos virão quando os jobs de
 * rastreamento dos Correios estiverem online em V2.
 */
export const deriveSaleDeliveryStatus = (saleItems, sale) => {
  const items = Array.isArray(saleItems) ? saleItems : [];
  const metadata = sale?.metadata || {};

  // 1) Reportados — erros de validação detectados (CPF inválido, CEP, etc).
  if (metadata.validation_errors && metadata.validation_errors.length > 0) {
    return 'reportados';
  }

  if (items.length === 0) {
    // Sem items conhecidos: cair em 'expedicao' (estado neutro) se paga,
    // ou 'reportados' caso payment_status pending (devia ter virado item).
    return sale?.payment_status === 'paid' ? 'expedicao' : 'reportados';
  }

  // Helpers de agregação
  const allHave = (predicate) => items.every(predicate);
  const someHas = (predicate) => items.some(predicate);

  const isTerminalCancelled = (it) => it.status === 'cancelled' || it.status === 'refunded';
  const isDelivered = (it) => it.status === 'delivered';

  // 2) Frustrados — todos itens cancelados/estornados.
  if (allHave(isTerminalCancelled)) return 'frustrados';

  // 3) Entregue / Pagos — todos itens entregues.
  if (allHave(isDelivered)) {
    if (sale?.payment_status === 'paid') return 'pagos';
    if (sale?.payment_status === 'partially_refunded' || sale?.payment_status === 'refunded') {
      return 'inadimplencia';
    }
    return 'entregue';
  }

  // 4) Requer atenção — qualquer falha de entrega ou retorno.
  if (someHas((it) => it.status === 'failed_attempt' || it.status === 'returned')) {
    return 'requer_atencao';
  }

  // 5) Retirar nos Correios.
  if (someHas((it) => it.status === 'available_for_pickup')) {
    return 'retirar_correios';
  }

  // 6) Saiu para entrega — tracking flag em metadata do item.
  if (someHas((it) => (it.metadata?.tracking_status) === 'out_for_delivery')) {
    return 'saiu_entrega';
  }

  // 7) Enviados — item com status shipped.
  if (someHas((it) => it.status === 'shipped')) {
    return 'enviados';
  }

  // 8) Expedição — item processando (com ou sem tracking_code).
  if (someHas((it) => it.status === 'processing' || !!it.tracking_code)) {
    return 'expedicao';
  }

  // 9) Default — venda paga aguardando processar.
  return 'expedicao';
};

// Estilos para o placeholder e layout
const placeholderStyles = `
  ::placeholder {
    font-size: 14px !important;
    color: #6c757d !important;
  }
  ::-webkit-input-placeholder {
    font-size: 14px !important;
    color: #6c757d !important;
  }
  ::-moz-placeholder {
    font-size: 14px !important;
    color: #6c757d !important;
  }
  :-ms-input-placeholder {
    font-size: 14px !important;
    color: #6c757d !important;
  }
  
  /* REMOVER BLOQUEIO - Deixar scroll natural */
`;





const AfterPayLayer = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('geral');
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Debug: verificar se dark mode está ativo
  useEffect(() => {
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
    console.log('🌙 Dark Mode Status:', isDarkMode ? 'ATIVO' : 'INATIVO');
    console.log('🎨 Theme Attribute:', document.documentElement.getAttribute('data-theme'));
    
    // Forçar reload dos estilos quando o tema muda
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          console.log('🔄 Tema mudou para:', document.documentElement.getAttribute('data-theme'));
        }
      });
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    
    return () => observer.disconnect();
  }, []);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  // KAN-13: estado do drawer de cancelar envio
  const [cancelTarget, setCancelTarget] = useState(null);
  // KAN-12 / Pacote 5: estado do drawer de reportar pedido
  const [reportTarget, setReportTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('30'); // Default 30 dias
  
  // Estados para Tags
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [selectedCardForTags, setSelectedCardForTags] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [colorPalette, setColorPalette] = useState([]);
  


  // Estados para integração com backend
  const [kanbanColumns, setKanbanColumns] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    delivered: 0,
    issues: 0,
    revenue: 0,
    todaySales: { count: 0, percentage: "0%", trend: "neutral" },
    weekSales: { count: 0, percentage: "0%", trend: "neutral" },
    monthSales: { count: 0, percentage: "0%", trend: "neutral" }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estrutura padrão das 10 colunas do AfterPay (spec v2.1 KAN-08:
  //   id 'expedicao' (sem tracking) → display "EM PROCESSAMENTO"
  //   id 'enviados' (com tracking, antes de saiu_entrega) → display "EXPEDIÇÃO"
  // IDs mantidos pra preservar drag&drop + sincronia com VIEW SQL).
  const buildEmptyColumns = () => [
    { id: 'reportados', title: 'REPORTADOS', color: '#ffc107', icon: 'mdi:alert-circle', cards: [] },
    { id: 'expedicao', title: 'EM PROCESSAMENTO', color: '#6c757d', icon: 'mdi:progress-clock', cards: [] },
    { id: 'enviados', title: 'EXPEDIÇÃO', color: '#0dcaf0', icon: 'mdi:package-variant-closed', cards: [] },
    { id: 'saiu_entrega', title: 'SAIU PARA ENTREGA', color: '#6610f2', icon: 'mdi:truck-fast', cards: [] },
    // KAN-02: REQUER ATENÇÃO vem ANTES de RETIRAR NOS CORREIOS (spec v2.1).
    { id: 'requer_atencao', title: 'REQUER ATENÇÃO', color: '#dc3545', icon: 'mdi:alert-decagram', cards: [] },
    { id: 'retirar_correios', title: 'RETIRAR NOS CORREIOS', color: '#6c757d', icon: 'mdi:store-marker', cards: [] },
    { id: 'entregue', title: 'ENTREGUE', color: '#198754', icon: 'mdi:check-circle', cards: [] },
    { id: 'inadimplencia', title: 'INADIMPLÊNCIA', color: '#fd7e14', icon: 'mdi:currency-usd-off', cards: [] },
    { id: 'frustrados', title: 'FRUSTRADOS', color: '#dc3545', icon: 'mdi:close-circle', cards: [] },
    { id: 'pagos', title: 'PAGOS', color: '#28a745', icon: 'mdi:cash-check', cards: [] },
  ];

  // Labels de status exibidos no badge do card (spec v2.1 KAN-08).
  const COLUMN_STATUS_LABEL = {
    reportados: { text: 'Reportado', color: 'warning' },
    expedicao: { text: 'Em processamento', color: 'secondary' },
    enviados: { text: 'Em expedição', color: 'info' },
    saiu_entrega: { text: 'Saiu para entrega', color: 'primary' },
    retirar_correios: { text: 'Aguardando retirada', color: 'secondary' },
    requer_atencao: { text: 'Requer atenção', color: 'danger' },
    entregue: { text: 'Entregue', color: 'success' },
    inadimplencia: { text: 'Inadimplente', color: 'danger' },
    frustrados: { text: 'Frustrado', color: 'danger' },
    pagos: { text: 'Pago', color: 'success' },
  };

  /**
   * Mapeia uma venda V2 (com items carregados) para o shape do card do Kanban.
   * O card V1 espera: { id, name, description, status, statusColor, date, avatar, tags,
   *                     metadata, saleStatus, totalAmount }.
   */
  const mapV2SaleToCard = (sale, columnId, productNamesByPhysicalId) => {
    const items = sale.items || [];
    // "description" no card V1 é uma lista textual dos produtos.
    const description = items.length > 0
      ? items
        .map((it) => {
          const baseName = productNamesByPhysicalId.get(it.physical_product_id) || 'Produto';
          return it.quantity > 1 ? `${it.quantity}x ${baseName}` : baseName;
        })
        .join(' + ')
      : 'AfterPay';

    const updatedAt = sale.updated_at || sale.created_at;
    const dateStr = updatedAt
      ? new Date(updatedAt).toLocaleDateString('pt-BR') + ' - '
        + new Date(updatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      : '';

    const statusLabel = COLUMN_STATUS_LABEL[columnId] || { text: '', color: 'secondary' };

    return {
      id: sale.id,
      saleUuid: sale.uuid,
      name: sale.customer_name || 'Cliente',
      email: sale.customer_email,
      phone: sale.customer_phone,
      avatar: null, // V2 não expõe avatar do cliente — fallback no card usa inicial
      description,
      status: statusLabel.text,
      statusColor: statusLabel.color,
      date: dateStr,
      lastUpdateDate: updatedAt,
      // total em centavos → reais via CurrencyFormatter.
      totalAmount: sale.total_amount_cents,
      totalFormatted: CurrencyFormatter.centsToFormatted(sale.total_amount_cents || 0),
      paymentStatus: sale.payment_status,
      saleStatus: null, // V2 não tem mais sales.sale_status
      metadata: sale.metadata || {},
      tags: [], // tags do card vivem em tabela própria; carregado pelo serviço dedicado quando aplicável
      validationErrors: sale.metadata?.validation_errors || [],
    };
  };

  /**
   * mapV2SaleItemToCard — 1 card POR sale_item (§9.2 KAN-13/KAN-17).
   *
   * Cada produto físico vendido vira um card independente. Quando a venda
   * tem múltiplos items, cada card ganha um indicador "Venda #X · n/N"
   * (KAN-17) + cor de borda derivada do sale_id pra agrupar visualmente.
   */
  const mapV2SaleItemToCard = (item, sale, itemIndex, totalItemsInSale, productNamesByPhysicalId) => {
    const columnId = deriveSaleItemColumn(item, sale);
    const baseName = productNamesByPhysicalId.get(item.physical_product_id) || 'Produto';
    const description = item.quantity > 1 ? `${item.quantity}x ${baseName}` : baseName;
    const updatedAt = item.updated_at || sale.updated_at || sale.created_at;
    const dateStr = updatedAt
      ? new Date(updatedAt).toLocaleDateString('pt-BR') + ' - '
        + new Date(updatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      : '';
    const statusLabel = COLUMN_STATUS_LABEL[columnId] || { text: '', color: 'secondary' };

    // KAN-17: cor de borda determinística por sale_id (hash → HSL)
    const sharedBorderHue = ((sale.id || 0) * 137) % 360;
    const sharedBorderColor = `hsl(${sharedBorderHue}, 70%, 55%)`;
    const isShared = totalItemsInSale > 1;

    // KAN-07 — calcula dias desde "disponível para retirada" pra cards em RETIRAR NOS CORREIOS.
    // Procura a data em: item.metadata.awaiting_pickup_since OU sale.metadata. Default = null.
    const itemMeta = item.metadata || {};
    const pickupSinceISO = itemMeta.awaiting_pickup_since
      || itemMeta.available_for_pickup_at
      || sale.metadata?.awaiting_pickup_since
      || null;
    let pickupDays = null;
    if (pickupSinceISO && item.status === 'available_for_pickup') {
      const since = new Date(pickupSinceISO).getTime();
      if (!Number.isNaN(since)) {
        pickupDays = Math.floor((Date.now() - since) / (1000 * 60 * 60 * 24));
      }
    }

    return {
      // id único do card = saleId-itemId pra evitar colisão entre items de mesma venda
      id: `${sale.id}-${item.id}`,
      saleId: sale.id,
      saleItemId: item.id,
      saleUuid: sale.uuid,
      name: sale.customer_name || 'Cliente',
      email: sale.customer_email,
      phone: sale.customer_phone,
      avatar: null,
      description,
      status: statusLabel.text,
      statusColor: statusLabel.color,
      date: dateStr,
      lastUpdateDate: updatedAt,
      // valor do item, não da venda inteira (KAN-17 mostra parte)
      totalAmount: item.allocated_amount_cents || item.unit_price_cents,
      totalFormatted: CurrencyFormatter.centsToFormatted(
        item.allocated_amount_cents || item.unit_price_cents || 0,
      ),
      paymentStatus: sale.payment_status,
      itemStatus: item.status,
      trackingCode: item.tracking_code || null,
      cancelledAt: item.cancelled_at || null,
      cancelReason: item.cancel_reason || null,
      metadata: { ...(sale.metadata || {}), item_metadata: item.metadata || {} },
      tags: [],
      validationErrors: sale.metadata?.validation_errors || [],
      // KAN-17 — indicador de venda compartilhada
      kan17: {
        isShared,
        position: itemIndex + 1,
        total: totalItemsInSale,
        badge: isShared ? `Venda #${sale.id} · ${itemIndex + 1}/${totalItemsInSale}` : null,
        borderColor: isShared ? sharedBorderColor : null,
        tooltip: isShared ? `Parte da venda #${sale.id}` : null,
      },
      // KAN-07 — dias aguardando retirada (apenas pra cards em RETIRAR NOS CORREIOS)
      kan07: {
        pickupDays,
        alertLevel: pickupDays != null
          ? (pickupDays >= 3 ? 'alert' : 'neutral')
          : null,
      },
    };
  };

  // Estrutura padrão da aba Frustrações (spec v2.1 KAN-03/04/05/09/16).
  // 6 colunas; sempre renderizamos as 6 mesmo que vazias.
  const buildEmptyFrustrationColumns = () => [
    { id: 'frustracoes', title: 'PAGAMENTOS FRUSTRADOS', color: '#dc3545', icon: 'mdi:alert-circle', cards: [] },
    { id: 'roubos', title: 'ROUBOS E EXTRAVIOS', color: '#6c757d', icon: 'mdi:shield-alert', cards: [] },
    { id: 'retornado', title: 'RETORNADO', color: '#0dcaf0', icon: 'mdi:package-variant-closed', cards: [] },
    { id: 'pending_requests', title: 'SOLICITAÇÕES PENDENTES', color: '#0d6efd', icon: 'mdi:clipboard-text-clock-outline', cards: [] },
    { id: 'devolvidos-reverso', title: 'DEVOLUÇÃO REVERSA', color: '#fd7e14', icon: 'mdi:package-variant-closed-remove', cards: [] },
    { id: 'cancelado-sem-custo', title: 'CANCELADOS', color: '#6c757d', icon: 'mdi:cancel', cards: [] },
  ];

  // Função para carregar dados do backend V2.
  // Geral: usa o pipeline V2 (productsV2API.listMine → salesV2API.listByProduct → findById).
  // Frustrações: mesmo pipeline, mas roteia cards cancelled/refunded por metadata.frustration_type.
  //   - V1 endpoint /api/afterpay/orders?tab=frustracoes foi removido junto com vw_afterpay_kanban
  //     no schema V2 (dropped). Não chamamos mais.
  const loadAfterPayData = async () => {
    try {
      setLoading(true);
      setError(null);

      // ============ Pipeline V2 (compartilhado pelas 2 abas) ============
      // Passo 1: produtos do usuário.
      const productsRes = await productsV2API.listMine({ limit: 200 });
      const products = productsRes.data?.products || [];

      if (products.length === 0) {
        setKanbanColumns(buildEmptyColumns());
        setFrustrationKanbanColumns(buildEmptyFrustrationColumns());
        return;
      }

      // Passo 2: busca vendas em paralelo de todos os produtos.
      const productSalesArrays = await Promise.all(
        products.map((p) =>
          salesV2API
            .listByProduct(p.id, { limit: 200 })
            .then((res) => res.data?.sales || [])
            .catch(() => []),
        ),
      );
      const allSales = productSalesArrays.flat();

      // Passo 3: filtra só AfterPay (payment_method='afterpay').
      // Backwards-compat: alguns registros antigos podem ter
      // payment_method='after_pay' ou is_afterpay no metadata.
      const afterpaySales = allSales.filter((s) =>
        s.payment_method === 'afterpay'
        || s.payment_method === 'after_pay'
        || s.metadata?.is_afterpay === true,
      );

      if (afterpaySales.length === 0) {
        setKanbanColumns(buildEmptyColumns());
        setFrustrationKanbanColumns(buildEmptyFrustrationColumns());
        return;
      }

      // Passo 4: hidrata cada venda com sale_items via findById.
      // (V2 listByProduct retorna só a linha de `sales`; items vêm via findById.)
      const hydratedSales = await Promise.all(
        afterpaySales.map((s) =>
          salesV2API
            .findById(s.id)
            .then((res) => res.data?.sale || { ...s, items: [] })
            .catch(() => ({ ...s, items: [] })),
        ),
      );

      // Passo 5: monta cache de nome de produto físico pra description do card.
      // (Buscamos só os physical_product_ids referenciados pelos items.)
      const physicalIds = new Set();
      hydratedSales.forEach((s) => {
        (s.items || []).forEach((it) => {
          if (it.physical_product_id) physicalIds.add(it.physical_product_id);
        });
      });

      // O nome do produto físico não vem nos items — derivamos a partir
      // do nome do produto pai (fallback "Produto"). Como cada produto pai
      // pode ter N físicos, esse cache é simplificado: usa o `name` da `sales`
      // table só se o backend expor; senão usa "Produto".
      // (Suficiente pra description; refinamento opcional carregando
      //  physicalProductsV2API.listByProduct por produto se virar UX-relevante.)
      const productNamesByPhysicalId = new Map();
      // Heurística simples: derivamos do nome do produto pai mais o id físico.
      // A `description` no V1 também era textual livre, então aceita string genérica.
      const productById = new Map(products.map((p) => [p.id, p]));
      hydratedSales.forEach((s) => {
        const pid = s.product_id;
        const parent = productById.get(pid);
        (s.items || []).forEach((it) => {
          if (!productNamesByPhysicalId.has(it.physical_product_id)) {
            productNamesByPhysicalId.set(
              it.physical_product_id,
              parent?.name || 'Produto',
            );
          }
        });
      });

      // Passo 6: deriva coluna + monta card.
      const columns = buildEmptyColumns();
      const columnsById = new Map(columns.map((c) => [c.id, c]));

      // KAN-13 / §9.2: 1 card POR sale_item (não por sale).
      // Cada item da venda vira 1 card independente. Quando há múltiplos
      // items na mesma venda, cada card ganha indicador KAN-17 ("1/N").
      hydratedSales.forEach((sale) => {
        const items = sale.items || [];
        if (items.length === 0) {
          // Venda sem items hidratados: fallback pro mapeamento agregado antigo
          // (cobre casos transitórios; gera 1 card único pra venda).
          const columnId = deriveSaleDeliveryStatus(items, sale);
          const card = mapV2SaleToCard(sale, columnId, productNamesByPhysicalId);
          const col = columnsById.get(columnId);
          if (col) col.cards.push(card);
          return;
        }
        items.forEach((item, idx) => {
          const card = mapV2SaleItemToCard(item, sale, idx, items.length, productNamesByPhysicalId);
          const columnId = deriveSaleItemColumn(item, sale);
          const col = columnsById.get(columnId);
          if (col) col.cards.push(card);
        });
      });

      // Passo 7: ordena cards dentro de cada coluna por data DESC.
      columns.forEach((c) => {
        c.cards.sort((a, b) => {
          const ta = a.lastUpdateDate ? new Date(a.lastUpdateDate).getTime() : 0;
          const tb = b.lastUpdateDate ? new Date(b.lastUpdateDate).getTime() : 0;
          return tb - ta;
        });
      });

      setKanbanColumns(columns);

      // Passo 7b: monta aba Frustrações a partir dos cards já caídos em "frustrados"
      // na aba Geral (deriveSaleItemColumn já move cancelled/refunded pra 'frustrados').
      // Aqui só roteamos por metadata.frustration_type pras 6 colunas da spec v2.1.
      const frustradosPool = columns.find((c) => c.id === 'frustrados')?.cards || [];
      const frustrationCols = buildEmptyFrustrationColumns();
      const frustrationByType = new Map(frustrationCols.map((c) => [c.id, c]));
      const FRUSTRATION_TYPE_TO_COL = {
        theft: 'roubos',
        returned: 'retornado',
        pending_request: 'pending_requests',
        returned_with_refund: 'devolvidos-reverso',
        cancelled_no_cost: 'cancelado-sem-custo',
      };
      frustradosPool.forEach((card) => {
        const ftype = card.metadata?.item_metadata?.frustration_type
          || card.metadata?.frustration_type
          || null;
        const targetId = FRUSTRATION_TYPE_TO_COL[ftype] || 'frustracoes';
        const col = frustrationByType.get(targetId);
        if (col) col.cards.push(card);
      });
      frustrationCols.forEach((c) => {
        c.cards.sort((a, b) => {
          const ta = a.lastUpdateDate ? new Date(a.lastUpdateDate).getTime() : 0;
          const tb = b.lastUpdateDate ? new Date(b.lastUpdateDate).getTime() : 0;
          return tb - ta;
        });
      });
      setFrustrationKanbanColumns(frustrationCols);

      // Stats simples a partir das vendas carregadas (mantém shape antigo).
      const totalCount = hydratedSales.length;
      const deliveredCount = columnsById.get('entregue').cards.length
        + columnsById.get('pagos').cards.length;
      const pendingCount = columnsById.get('expedicao').cards.length
        + columnsById.get('enviados').cards.length
        + columnsById.get('saiu_entrega').cards.length;
      const issuesCount = columnsById.get('requer_atencao').cards.length
        + columnsById.get('reportados').cards.length
        + columnsById.get('frustrados').cards.length;
      const revenueCents = hydratedSales.reduce(
        (sum, s) => sum + (s.total_amount_cents || 0),
        0,
      );

      setStats((prev) => ({
        ...prev,
        total: totalCount,
        pending: pendingCount,
        processing: pendingCount,
        delivered: deliveredCount,
        issues: issuesCount,
        revenue: revenueCents,
      }));
    } catch (err) {
      console.error('Erro ao carregar dados do AfterPay (V2):', err);
      setError(err?.message || 'Erro ao carregar pedidos');
      setKanbanColumns(buildEmptyColumns());
      setFrustrationKanbanColumns(buildEmptyFrustrationColumns());
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados quando o componente montar ou quando filtros mudarem
  useEffect(() => {
    loadAfterPayData();
  }, [activeTab, searchTerm, dateFilter]);

  // Carregar tags disponíveis e paleta de cores ao montar
  useEffect(() => {
    loadAvailableTags();
    setColorPalette(cardTagsService.getColorPalette());
  }, []);

  // Função para carregar tags disponíveis
  const loadAvailableTags = async () => {
    try {
      const tags = await cardTagsService.getProducerTags();
      setAvailableTags(tags);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  // Abrir modal de tags
  const handleManageTags = (card) => {
    setSelectedCardForTags(card);
    setShowTagsModal(true);
  };

  // Criar nova tag
  const handleCreateTag = async (name, color) => {
    try {
      await cardTagsService.createTag(name, color);
      await loadAvailableTags(); // Recarregar tags
    } catch (error) {
      throw error;
    }
  };

  // Adicionar tag ao card
  const handleAddTagToCard = async (cardId, tagId) => {
    try {
      await cardTagsService.addTagToCard(cardId, tagId);
      await loadAfterPayData(); // Recarregar kanban para atualizar tags nos cards
    } catch (error) {
      throw error;
    }
  };

  // Remover tag do card
  const handleRemoveTagFromCard = async (cardId, tagId) => {
    try {
      await cardTagsService.removeTagFromCard(cardId, tagId);
      await loadAfterPayData(); // Recarregar kanban para atualizar tags nos cards
    } catch (error) {
      throw error;
    }
  };

  // Adicionar estilos CSS para o placeholder
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = placeholderStyles;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  // Formatador de moeda local para valores em centavos
  const formatCurrencyLocal = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100); // Convertendo de centavos para reais
  };



  // Dados do kanban (columns) - seguindo exatamente a imagem
  const columns = [
    {
      id: 'reportados',
      title: 'REPORTADOS',
      color: 'warning',
      icon: 'mdi:alert-circle',
      cards: [
        {
          id: '1',
          name: 'Ana Paula Silva',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Italiano',
          status: 'Em análise',
          statusColor: 'warning',
          date: '25/03/2025',
          avatar: '/assets/images/avatar/avatar1.png'
        },
        {
          id: '2',
          name: 'João Carlos Santos',
          description: '01 Pote Angolano + 02 Pote Francês + 01 Pote Alemão',
          status: 'Em análise',
          statusColor: 'warning',
          date: '25/03/2025',
          avatar: '/assets/images/avatar/avatar2.png'
        },
        {
          id: '3',
          name: 'Maria Fernanda Costa',
          description: '03 Pote Angolano + 01 Pote Francês + 02 Pote Espanhol',
          status: 'Em análise',
          statusColor: 'warning',
          date: '25/03/2025',
          avatar: '/assets/images/avatar/avatar-group1.png'
        },
        {
          id: '4',
          name: 'Pedro Henrique Lima',
          description: '01 Pote Angolano + 01 Pote Francês + 01 Pote Português',
          status: 'Em análise',
          statusColor: 'warning',
          date: '25/03/2025',
          avatar: '/assets/images/avatar/avatar-group2.png'
        },
        {
          id: '5',
          name: 'Lucia Oliveira Martins',
          description: '02 Pote Angolano + 02 Pote Francês + 01 Pote Holandês',
          status: 'Em análise',
          statusColor: 'warning',
          date: '25/03/2025',
          avatar: '/assets/images/avatar/avatar-group3.png'
        }
      ]
    },
    {
      id: 'enviados',
      title: 'ENVIADOS',
      color: 'info',
      icon: 'mdi:truck-delivery',
      cards: [
        {
          id: '6',
          name: 'Roberto Almeida Souza',
          description: '01 Pote Angolano + 01 Pote Francês + 01 Pote Russo',
          status: 'Preparando',
          statusColor: 'info',
          date: '23/03/2025',
          avatar: '/assets/images/avatar/avatar-group4.png'
        },
        {
          id: '7',
          name: 'Fernanda Rodrigues Pereira',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Sueco',
          status: 'Preparando',
          statusColor: 'info',
          date: '23/03/2025',
          avatar: '/assets/images/avatar/avatar-group5.png'
        }
      ]
    },
    {
      id: 'saiu-entrega',
      title: 'SAIU PARA ENTREGA',
      color: 'warning',
      icon: 'mdi:truck-fast',
      cards: [
        {
          id: '8',
          name: 'Carlos Eduardo Ferreira',
          description: '01 Pote Angolano + 02 Pote Francês + 01 Pote Dinamarquês',
          status: 'Em rota',
          statusColor: 'warning',
          date: '24/03/2025',
          avatar: '/assets/images/avatar/avatar-group6.png'
        },
        {
          id: '9',
          name: 'Amanda Costa Silva',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Norueguês',
          status: 'Em rota',
          statusColor: 'warning',
          date: '24/03/2025',
          avatar: '/assets/images/avatar/avatar-shape1.png'
        },
        {
          id: '10',
          name: 'Ricardo Mendes Oliveira',
          description: '01 Pote Angolano + 01 Pote Francês + 02 Pote Finlandês',
          status: 'Em rota',
          statusColor: 'warning',
          date: '24/03/2025',
          avatar: '/assets/images/avatar/avatar-shape2.png'
        }
      ]
    },
    {
      id: 'correios',
      title: 'RETIRAR NOS CORREIOS',
      color: 'success',
      icon: 'mdi:package-variant',
      cards: [
        {
          id: '11',
          name: 'Patricia Santos Lima',
          description: '01 Pote Angolano + 01 Pote Francês + 01 Pote Islandês',
          status: 'Pronto para retirada',
          statusColor: 'success',
          date: '23/03/2025',
          avatar: '/assets/images/avatar/avatar-shape3.png'
        },
        {
          id: '12',
          name: 'Marcelo Silva Costa',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Polonês',
          status: 'Pronto para retirada',
          statusColor: 'success',
          date: '23/03/2025',
          avatar: '/assets/images/avatar/avatar-shape4.png'
        }
      ]
    },
    {
      id: 'atencao',
      title: 'REQUER ATENÇÃO',
      color: 'danger',
      icon: 'mdi:alert-decagram',
      cards: [
        {
          id: '13',
          name: 'Juliana Ferreira Costa',
          description: '01 Pote Angolano + 01 Pote Francês + 01 Pote Tcheco',
          status: 'Falha na entrega',
          statusColor: 'danger',
          date: '23/03/2025',
          avatar: '/assets/images/avatar/avatar-shape5.png'
        },
        {
          id: '14',
          name: 'Thiago Oliveira Santos',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Húngaro',
          status: 'Falha na entrega',
          statusColor: 'danger',
          date: '23/03/2025',
          avatar: '/assets/images/avatar/avatar-shape6.png'
        }
      ]
    },
    {
      id: 'entregues',
      title: 'ENTREGUES',
      color: 'success',
      icon: 'mdi:check-circle',
      cards: [
        {
          id: '15',
          name: 'Vanessa Lima Silva',
          description: '01 Pote Angolano + 01 Pote Francês + 01 Pote Romeno',
          status: 'Entregue',
          statusColor: 'success',
          date: '22/03/2025',
          avatar: '/assets/images/avatar/avatar-shape7.png'
        },
        {
          id: '16',
          name: 'Daniel Costa Oliveira',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Búlgaro',
          status: 'Entregue',
          statusColor: 'success',
          date: '22/03/2025',
          avatar: '/assets/images/avatar/avatar-shape8.png'
        },
        {
          id: '17',
          name: 'Carolina Santos Ferreira',
          description: '01 Pote Angolano + 02 Pote Francês + 01 Pote Croata',
          status: 'Entregue',
          statusColor: 'success',
          date: '22/03/2025',
          avatar: '/assets/images/avatar/avatar-shape9.png'
        },
        {
          id: '18',
          name: 'Lucas Mendes Costa',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Sérvio',
          status: 'Entregue',
          statusColor: 'success',
          date: '22/03/2025',
          avatar: '/assets/images/avatar/avatar-shape10.png'
        }
      ]
    },
    {
      id: 'inadimplentes',
      title: 'INADIMPLENTES',
      color: 'danger',
      icon: 'mdi:account-alert',
      cards: [
        {
          id: '19',
          name: 'Rafael Silva Costa',
          description: '01 Pote Angolano + 01 Pote Francês + 01 Pote Esloveno',
          status: 'Entregue Cobrado',
          statusColor: 'danger',
          date: '21/03/2025',
          avatar: '/assets/images/avatar/avatar-shape11.png'
        },
        {
          id: '20',
          name: 'Isabela Ferreira Santos',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Eslovaco',
          status: 'Entregue Cobrado',
          statusColor: 'danger',
          date: '21/03/2025',
          avatar: '/assets/images/avatar/avatar-shape12.png'
        }
      ]
    },
    {
      id: 'frustrados',
      title: 'FRUSTRADOS',
      color: 'danger',
      icon: 'mdi:close-circle',
      cards: [
        {
          id: '21',
          name: 'Gabriel Costa Lima',
          description: '01 Pote Angolano + 01 Pote Francês + 01 Pote Lituano',
          status: 'Devolvido',
          statusColor: 'danger',
          date: '20/03/2025',
          avatar: '/assets/images/avatar/avatar-shape13.png'
        },
        {
          id: '22',
          name: 'Mariana Oliveira Silva',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Letão',
          status: 'Devolvido',
          statusColor: 'danger',
          date: '20/03/2025',
          avatar: '/assets/images/avatar/avatar-shape14.png'
        }
      ]
    },
    {
      id: 'pagos',
      title: 'PAGOS',
      color: 'success',
      icon: 'mdi:cash-multiple',
      cards: [
        {
          id: '23',
          name: 'Beatriz Santos Costa',
          description: '01 Pote Angolano + 01 Pote Francês + 01 Pote Estoniano',
          status: 'Entregue Pago',
          statusColor: 'success',
          date: '19/03/2025',
          avatar: '/assets/images/avatar/avatar-shape15.png'
        },
        {
          id: '24',
          name: 'André Lima Ferreira',
          description: '02 Pote Angolano + 01 Pote Francês + 01 Pote Maltês',
          status: 'Entregue Pago',
          statusColor: 'success',
          date: '19/03/2025',
          avatar: '/assets/images/avatar/avatar-shape16.png'
        }
      ]
    }
  ];

  // Função para filtrar cards baseado no termo de pesquisa
  const filterCards = (cards, searchTerm) => {
    if (!searchTerm.trim()) return cards;
    
    return cards.filter(card => 
      card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Aplicar filtro aos dados do Kanban (dados reais do backend)
  const filteredKanbanColumns = kanbanColumns.map(column => ({
    ...column,
    cards: filterCards(column.cards || [], searchTerm)
  }));



  const [frustrationKanbanColumns, setFrustrationKanbanColumns] = useState([]);

  // Função para lidar com drag & drop do Kanban geral
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const newColumns = Array.from(kanbanColumns);
    const sourceColumn = newColumns.find(col => col.id === source.droppableId);
    const destColumn = newColumns.find(col => col.id === destination.droppableId);

    if (sourceColumn && destColumn) {
      const [removed] = sourceColumn.cards.splice(source.index, 1);
      
      // Atualizar o status do card baseado na coluna de destino (10 colunas, KAN-08)
      const statusMap = {
        'reportados': 'Reportado',
        'expedicao': 'Em processamento',
        'enviados': 'Em expedição',
        'saiu_entrega': 'Saiu para entrega',
        'retirar_correios': 'Aguardando retirada',
        'requer_atencao': 'Requer atenção',
        'entregue': 'Entregue',
        'inadimplencia': 'Inadimplente',
        'frustrados': 'Frustrado',
        'pagos': 'Pago'
      };
      
      const statusColorMap = {
        'reportados': 'warning',
        'expedicao': 'info',
        'enviados': 'primary',
        'saiu_entrega': 'primary',
        'retirar_correios': 'secondary',
        'requer_atencao': 'danger',
        'entregue': 'success',
        'inadimplencia': 'danger',
        'frustrados': 'danger',
        'pagos': 'success'
      };

      // Atualizar status local
      removed.status = statusMap[destination.droppableId] || removed.status;
      removed.statusColor = statusColorMap[destination.droppableId] || removed.statusColor;
      
      destColumn.cards.splice(destination.index, 0, removed);
      setKanbanColumns(newColumns);
      
      // TODO: Aqui seria feita a chamada para o backend para atualizar o status no banco
      try {
        // const response = await fetch(`/api/orders/${removed.id}/status`, {
        //   method: 'PUT',
        //   headers: {
        //     'Content-Type': 'application/json',
        //     'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        //   },
        //   body: JSON.stringify({ 
        //     status: removed.status,
        //     category: destination.droppableId 
        //   })
        // });
        console.log(`Movido pedido ${removed.id} para ${removed.status}`);
      } catch (error) {
        console.error('Erro ao atualizar status no backend:', error);
        // TODO: Reverter mudança local em caso de erro
      }
    }
  };

  // Função para lidar com drag & drop do Kanban de frustrações
  const handleFrustrationDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const newColumns = Array.from(frustrationKanbanColumns);
    const sourceColumn = newColumns.find(col => col.id === source.droppableId);
    const destColumn = newColumns.find(col => col.id === destination.droppableId);

    if (sourceColumn && destColumn) {
      const [removed] = sourceColumn.cards.splice(source.index, 1);
      
      // Atualizar o status do card baseado na coluna de destino
      const statusMap = {
        'frustracoes': 'Frustrado',
        'roubos': 'Roubado', 
        'devolvidos-reverso': 'Devolvido',
        'retornado': 'Retornado',
        'cancelado-sem-custo': 'Cancelado'
      };
      
      const statusColorMap = {
        'frustracoes': 'danger',
        'roubos': 'dark',
        'devolvidos-reverso': 'warning', 
        'retornado': 'info',
        'cancelado-sem-custo': 'secondary'
      };

      // Atualizar status local
      removed.status = statusMap[destination.droppableId] || removed.status;
      removed.statusColor = statusColorMap[destination.droppableId] || removed.statusColor;
      
      destColumn.cards.splice(destination.index, 0, removed);
      setFrustrationKanbanColumns(newColumns);

      // ✅ NOVA: Chamada real para o backend para atualizar status
      try {
        const apiBase2 = API_URL;

        const authToken = localStorage.getItem('auth_token');
        if (!authToken) {
          // Sem token → redirect pra login (SEC-7)
          window.location.href = '/#/login';
          return;
        }
        const url = `${apiBase2}/api/afterpay/frustrations/${removed.id}/move`;

        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ 
            columnId: destination.droppableId,
            position: destination.index
          })
        });

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          const text = await response.text();
          throw new Error(text || `Resposta não JSON (${response.status})`);
        }

        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.message || 'Erro ao mover card');
        }
        
        console.log(`✅ Pedido ${removed.id} movido para ${destination.droppableId}:`, result);
      } catch (error) {
        console.error('❌ Erro ao atualizar status no backend:', error);
        // Reverter mudança local em caso de erro
        destColumn.cards.splice(destination.index, 1);
        sourceColumn.cards.splice(source.index, 0, removed);
        setFrustrationKanbanColumns([...newColumns]);
        
        alert(`Erro ao mover pedido: ${error.message}`);
      }
    }
  };

  // Controlar fullscreen
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar');
    const navbar = document.querySelector('.navbar');
    
    if (isFullscreen) {
      // Esconder sidebar e navbar
      if (sidebar) sidebar.style.display = 'none';
      if (navbar) navbar.style.display = 'none';
    } else {
      // Mostrar sidebar e navbar
      if (sidebar) sidebar.style.display = 'block';
      if (navbar) navbar.style.display = 'block';
    }
  }, [isFullscreen]);




  const handleCardClick = (card) => {
    // KAN-13: card.id agora pode ser "saleId-itemId" (string) quando vier
    // do mapV2SaleItemToCard. Pra abrir o modal de venda, usa o saleId.
    const saleId = card.saleId || card.id;
    setSelectedSaleId(saleId);
    setShowSaleModal(true);
  };

  // KAN-13: cancelar envio de 1 sale_item específico
  const handleCancelShipment = (card) => {
    if (!card?.saleItemId) {
      console.warn('[AfterPayLayer] Card sem saleItemId — cancelamento não disponível');
      return;
    }
    setCancelTarget(card);
  };
  const handleCancelled = () => {
    setCancelTarget(null);
    // Recarrega o Kanban pra refletir o cancelamento
    loadAfterPayData();
  };

  // KAN-12 / Pacote 5: reportar pedido — move pra coluna escolhida em Frustrações
  const handleReport = (card) => {
    if (!card?.saleItemId) {
      console.warn('[AfterPayLayer] Card sem saleItemId — reportar não disponível');
      return;
    }
    setReportTarget(card);
  };
  const handleReported = () => {
    setReportTarget(null);
    // Recarrega o Kanban pra refletir a mudança de coluna
    loadAfterPayData();
  };

  return (
    <div className={`${isFullscreen ? styles.fullscreen : ''} ${styles.kanbanContainer} ${activeTab === 'frustracoes' ? 'frustrationMode' : ''}`}>
      {/* Header: Título + Botões na Mesma Linha */}
      <div className={styles.kanbanHeader}>
        {/* Título à Esquerda */}
        <div className="d-flex align-items-center gap-3">
          <Icon icon="mdi:credit-card" style={{ fontSize: '22px', color: '#0d6efd' }} />
          <div>
            <span className={`mb-0 fw-bold ${styles.kanbanTitle}`} style={{ display: 'block' }}>
              Pedidos Afterpay
            </span>
            <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
              Gerencie suas transações e configurações do After Pay
            </p>
          </div>
        </div>
        
        {/* Botões de Ação à Direita */}
        <div className={styles.kanbanActions}>
                {/* Botão Logística AfterPay */}
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center"
                  onClick={() => navigate('/reports/afterpay')}
                  style={{
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    height: '44px',
                    gap: '6px'
                  }}
                >
                  <Icon icon="mdi:truck-delivery" style={{ fontSize: '1rem' }} />
                  <span>Logística AfterPay</span>
                </button>
                {/* Botão Filtros */}
                <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center"
                  onClick={() => setShowFiltersModal(true)}
                  style={{
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    height: '44px',
                    gap: '6px'
                  }}
                >
                  <Icon icon="mdi:filter-variant" style={{ fontSize: '1rem' }} />
                  <span>Filtros</span>
                </button>
                <button
                  className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '10px',
                    border: '2px solid #0d6efd',
                    backgroundColor: isFullscreen ? '#0d6efd' : undefined,
                    transition: 'all 0.3s ease',
                    boxShadow: isFullscreen ? '0 4px 12px rgba(13, 110, 253, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  title={isFullscreen ? "Sair da tela cheia" : "Ampliar"}
                  onMouseEnter={(e) => {
                    if (!isFullscreen) {
                      e.currentTarget.style.borderColor = '#0a58ca';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(13, 110, 253, 0.2)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isFullscreen) {
                      e.currentTarget.style.borderColor = '#0d6efd';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <Icon
                    icon={isFullscreen ? "mdi:fullscreen-exit" : "mdi:fullscreen"}
                    className={isFullscreen ? 'text-white' : 'text-primary'}
                    style={{
                      fontSize: '1.3rem',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </button>
        </div>
      </div>

      {/* Abas do Kanban - Geral / Frustrações */}
      <div className={styles.kanbanTabs}>
        <button
          type="button"
          className={`${styles.kanbanTab} ${activeTab === 'geral' ? styles.kanbanTabActive : ''}`}
          onClick={() => setActiveTab('geral')}
        >
          <Icon icon="mdi:view-list" style={{ fontSize: '1rem' }} />
          <span>Geral</span>
        </button>
        <button
          type="button"
          className={`${styles.kanbanTab} ${activeTab === 'frustracoes' ? styles.kanbanTabActive : ''}`}
          onClick={() => setActiveTab('frustracoes')}
        >
          <Icon icon="mdi:alert-circle" style={{ fontSize: '1rem' }} />
          <span>Frustrações</span>
        </button>
      </div>

      {/* Kanban Board */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="text-muted">Carregando pedidos AfterPay...</p>
                </div>
              </div>
            ) : error ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                  <Icon icon="mdi:alert-circle" className="text-danger mb-3" style={{ fontSize: '48px' }} />
                  <span className="text-danger mb-2 d-block fw-semibold" style={{ fontSize: '16px' }}>Erro ao carregar dados</span>
                  <p className="text-muted mb-3">{error}</p>
                  <button 
                    onClick={loadAfterPayData}
                    className="btn btn-primary"
                  >
                    <Icon icon="mdi:refresh" className="me-2" />
                    Tentar Novamente
                  </button>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'geral' ? (
                  <KanbanBoard columns={filteredKanbanColumns} onDragEnd={handleDragEnd} enableDrag={false} isFullscreen={isFullscreen} onCardClick={handleCardClick} onManageTags={handleManageTags} onCancelShipment={handleCancelShipment} onReport={handleReport} />
                ) : (
                  <KanbanBoard columns={frustrationKanbanColumns} onDragEnd={handleFrustrationDragEnd} enableDrag={true} isFullscreen={isFullscreen} onCardClick={handleCardClick} onManageTags={handleManageTags} onCancelShipment={handleCancelShipment} onReport={handleReport} />
                )}
              </>
            )}
      </div>
      
      {/* Modal de Detalhes do Pedido */}
      <OrderDetailsModal 
        show={showOrderModal}
        onHide={() => setShowOrderModal(false)}
        order={selectedOrder}
      />

      {/* Modal de Gerenciamento de Tags */}
      <TagsModal
        show={showTagsModal}
        onHide={() => setShowTagsModal(false)}
        cardId={selectedCardForTags?.id}
        cardTags={selectedCardForTags?.tags || []}
        availableTags={availableTags}
        colors={colorPalette}
        onCreateTag={handleCreateTag}
        onAddTagToCard={handleAddTagToCard}
        onRemoveTagFromCard={handleRemoveTagFromCard}
      />

      {/* Modal de Filtros */}
      <Modal 
        show={showFiltersModal} 
        onHide={() => setShowFiltersModal(false)} 
        size="lg" 
        centered
        backdrop="static"
        backdropClassName="modal-backdrop-lightbox"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <Icon icon="mdi:filter-variant" />
            Filtros de Busca
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            {/* Campo de Pesquisa */}
            <div className="col-12">
              <label className="form-label fw-bold">Buscar por:</label>
              <div className="input-group">
                <span className="input-group-text">
                  <Icon icon="mdi:magnify" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nome, email ou código de rastreio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            {/* Filtro de Período */}
            <div className="col-12">
              <label className="form-label fw-bold">Período:</label>
              <div className="input-group">
                <span className="input-group-text">
                  <Icon icon="mdi:calendar-range" />
                </span>
                <select
                  className="form-select"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="15">Últimos 15 dias</option>
                  <option value="30">Últimos 30 dias</option>
                  <option value="45">Últimos 45 dias</option>
                  <option value="60">Últimos 60 dias</option>
                  <option value="90">Últimos 90 dias</option>
                </select>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => {
              setSearchTerm('');
              setDateFilter('30');
              setShowFiltersModal(false);
            }}
          >
            Limpar Filtros
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={() => setShowFiltersModal(false)}
          >
            Aplicar Filtros
          </button>
        </Modal.Footer>
      </Modal>

      {/* Drawer de Detalhes da Venda */}
      <SaleDetailsDrawer
        show={showSaleModal}
        onHide={() => { setShowSaleModal(false); setSelectedSaleId(null); }}
        saleId={selectedSaleId}
        onSaleUpdated={() => {
          loadAfterPayData();
        }}
      />

      {/* KAN-13: Cancelar envio (1 sale_item por vez) */}
      <CancelShipmentDrawer
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        card={cancelTarget}
        onCancelled={handleCancelled}
      />

      {/* KAN-12 / Pacote 5: Reportar pedido (1 sale_item por vez) */}
      <ReportShipmentDrawer
        isOpen={!!reportTarget}
        onClose={() => setReportTarget(null)}
        card={reportTarget}
        onReported={handleReported}
      />
    </div>
  );
};

export default AfterPayLayer;
